import { RowTypes, ColumnTypes } from "./cell/CellConstants";
import { ProcessItemTypes, ProcessTypes } from "../../utils/icons";
import { ActiveCorners, DefaultHeights, DefaultWidths } from "./corner/CornerConstants";
import { isFormula } from "../../utils/util";
import { getCellId, sortRange, splitCellId } from "./cell/Utils";
import { hfInstance } from "../../store/Cells";

export default {
  methods: {
    getCellValue(rowIndex, columnIndex, sheetId) {
      sheetId = sheetId ?? this.currentSheet.sheetId;
      return hfInstance.getCellValue({ sheet: sheetId, row: rowIndex, col: columnIndex }) ?? "";
    },
    getCellText(rowIndex, columnIndex, sheetId) {
      sheetId = sheetId ?? this.currentSheet.sheetId;
      return hfInstance.getCellSerialized({ sheet: sheetId, row: rowIndex, col: columnIndex }) ?? "";
    },
    getCellIdByIndex(rowIndex, columnIndex) {
      const rowId = this.rows[rowIndex]?.id;
      const columnId = this.columns[columnIndex]?.id;
      if (!rowId || !columnId) return;
      return getCellId(rowId, columnId);
    },
    getCellIndexById(id) {
      return this.$store.getters["cells/getCellIndexById"](id);
    },
    getColumn(id) {
      return this.activeView?.columns[id];
    },
    getRow(id) {
      return this.activeView?.rows[id];
    },
    getWidth(id) {
      return this.getRawWidth(id) + "px";
    },
    // width without px
    getRawWidth(id) {
      const columnType = this.columnMap[id]?.type;
      const defaultWidth =
        this.current.type === ProcessTypes.Grid
          ? DefaultWidths.Grid
          : this.current.type === ProcessItemTypes.Posting && columnType === ColumnTypes.Player
          ? DefaultWidths.Default / 2
          : DefaultWidths.Default;

      if (this.isDesignModeActive) return DefaultWidths[id] || defaultWidth;

      const column = this.getColumn(id);
      const width = column?.show === "0" ? 0 : column?.size ?? DefaultWidths[id] ?? defaultWidth;
      return width;
    },
    getHeight(id) {
      return this.getRawHeight(id) + "px";
    },
    // Height without px
    getRawHeight(id) {
      const defaultHeight =
        this.current.type === ProcessTypes.Grid ? DefaultHeights.Grid : DefaultHeights[id] || DefaultHeights.Default;

      if (this.isDesignModeActive) return defaultHeight;

      const row = this.getRow(id);
      const height = row?.show === "0" ? 0 : row?.size || defaultHeight;
      return height;
    },

    getMergedCellWidth(mergedCell) {
      const range = this.getRangeByCellId(mergedCell);
      const columnIds = this.$store.getters["cells/columnsInRange"](range);
      return columnIds.reduce((totalWidth, columnId) => {
        return totalWidth + this.getRawWidth(columnId);
      }, 0);
    },

    getMergedCellHeight(mergedCell) {
      const range = this.getRangeByCellId(mergedCell);
      const rowIds = this.$store.getters["cells/rowsInRange"](range);

      return rowIds.reduce((totalHeight, rowId) => {
        return totalHeight + this.getRawHeight(rowId);
      }, 0);
    },
    getRowVisibility(id) {
      return this.$store.getters["cells/getRowVisibility"](id);
    },
    getColumnVisibility(id) {
      if (this.isDesignModeActive) return true;

      return !(this.getColumn(id)?.show === "0" || this.getColumn(id)?.size == 0);
    },
    getColumnBgColor(id) {
      return this.getColumn(id)?.backgroundColor;
    },
    getColumnFontColor(id) {
      return this.getColumn(id)?.fontColor;
    },
    getRowBgColor(id) {
      return this.getRow(id)?.backgroundColor;
    },
    getRowFontColor(id) {
      return this.getRow(id)?.fontColor;
    },
    getRangeByCellId(cellId) {
      const mergeId = cellId.includes("_merged") ? cellId : this.mergedCellIds[cellId];
      if (mergeId) {
        const [columnIndex, rowIndex] = this.getCellCoordinate(mergeId.replace("_merged", ""));
        const mergedCell = this.mergedCells[mergeId];
        const { rowSpan, colSpan } = this.$store.getters["cells/getRowAndColSpan"](mergedCell);
        return [columnIndex, rowIndex, columnIndex + colSpan - 1, rowIndex + rowSpan - 1];
      } else {
        const [columnIndex, rowIndex] = this.getCellCoordinate(cellId);
        return [columnIndex, rowIndex, columnIndex, rowIndex];
      }
    },
    getCellCoordinate(cellId) {
      return this.$store.getters["cells/getCellCoordinate"](cellId);
    },
    // get union of two ranges
    unifyRanges(range1, range2) {
      return [
        Math.min(range1[0], range2[0]),
        Math.min(range1[1], range2[1]),
        Math.max(range1[2], range2[2]),
        Math.max(range1[3], range2[3]),
      ];
    },
    // take into account merged cells in a range
    calculateRange(range) {
      if (range.length == 2) range = [...range, ...range];
      const visitedCells = {};
      let rangeIds = [];

      do {
        rangeIds = this.$store.getters["cells/rangeIds"](range).filter((item) => !visitedCells[item]);
        rangeIds.forEach((cellId) => {
          visitedCells[cellId] = true;
          const cellRange = this.getRangeByCellId(cellId);
          range = this.unifyRanges(range, cellRange);
        });
      } while (rangeIds.length > 0);

      return sortRange(range);
    },
  },
  computed: {
    columnMap() {
      return this.design.columnMap;
    },
    rowMap() {
      return this.design.rowMap;
    },
    pdesign() {
      return this.$store.state.pdesign;
    },
    flowEditorVisible() {
      return this.getColumnVisibility("Flow");
    },
    activityBeingLinked() {
      return this.pdesign.activityBeingLinked;
    },
    design() {
      return this.current.design;
    },
    preferences() {
      return this.$store.state.preferences;
    },
    players() {
      return this.current.design.columns.filter((item) => item.type === ColumnTypes.Player);
    },
    activities() {
      return this.current.design.rows.filter((item) => item.type === RowTypes.Activity);
    },
    toolbox() {
      return this.$store.state.toolbox;
    },
    current() {
      return this.$store.state.cells.current;
    },
    isTypePosting() {
      return this.current.type == "posting";
    },
    readonly() {
      return (
        this.$store.state.teams.current?.members?.filter(
          (member) => member.email == this.authUser.email && ["owner", "admin", "editor"].indexOf(member.role) > -1
        ).length == 0
      );
    },
    viewModeVerticalShadow() {
      return this.isDataViewActive && this.pdesign.shadow.vertical;
    },
    isNavCornerActive() {
      return this.views.activeCorner === ActiveCorners.NavigationCorner;
    },
    isStyleCornerActive() {
      return this.views.activeCorner === ActiveCorners.StyleCorner;
    },
    isDataViewActive() {
      return this.views.activeCorner === null;
    },
    isDesignModeActive() {
      return this.isNavCornerActive || this.isStyleCornerActive;
    },
    views() {
      return this.$store.state.views;
    },
    activeView() {
      return this.views.activeView;
    },
    rows() {
      return this.current?.design.rows;
    },
    columns() {
      return this.current?.design.columns;
    },
    cellsMetaData() {
      return this.current?.design.cellsMetaData;
    },
    currentSheet() {
      return this.$store.state.sheets.currentSheet;
    },
    allSheets() {
      return this.$store.state.sheets.allSheets;
    },
    mainRange() {
      return this.$store.getters["cells/mainRange"];
    },
    rangeDragging() {
      return this.$store.state.cells.rangeDragging;
    },
    startDragging() {
      return this.$store.state.cells.startDragging;
    },
    rangeSelectorCollapse() {
      return this.$store.state.cells.rangeSelectorCollapse;
    },
    activeCellId() {
      return this.$store.state.cells.activeCellId;
    },
    visiblePlayers() {
      return this.players.filter((id) => this.getColumnVisibility(id));
    },
    processFlowWidth() {
      return this.getRawWidth("Flow");
    },
    mergedCells() {
      return this.current.design.mergedCells || {};
    },
    mergedCellsArray() {
      return Object.values(this.mergedCells);
    },
    mergedCellIds() {
      return this.$store.state.cells.mergedCellIds;
    },
    tableHeaders() {
      return this.$store.state.table.headers;
    },
    redrawCanvas() {
      return this.$store.state.cells.redrawCanvas;
    },
  },
};
